import React from 'react';
import _ from 'lodash';

import { PAGE, SECTION_COLUMN, CONTAINER, CAROUSEL_CARD } from 'constants/widgetTypes';
import { updateWidgetProperty } from 'actions/editor';
import { getCardFlexConfig } from '../carousel/card/flexConfig';
import { capPaddingSides } from './flexCss';

export {
  flexValueToCss,
  pxToNumber,
  numberToPx,
  resolvePadding,
  paddingToStored,
  paddingToCss,
  capPaddingSides,
  MOBILE_MAX_PADDING,
} from './flexCss';

export const DEFAULT_FLEX_LAYOUT = {
  flexDirection: 'row',
  justifyContent: 'flexStart',
  alignItems: 'flexStart',
  alignContent: 'flexStart',
  flexWrap: 'wrap',
  gap: '0px',
  padding: { topLeft: '0px', topRight: '0px', bottomLeft: '0px', bottomRight: '0px', lock: true }
};

export const PAGE_DEFAULT_FLEX_LAYOUT = {
  ...DEFAULT_FLEX_LAYOUT,
  alignItems: 'flexStart'
};

export const MOBILE_FLEX_DEFAULTS = {
  flexDirection: 'column'
};

export function resolveFlexLayout(widgetProps, breakpoint, baseDefaults = DEFAULT_FLEX_LAYOUT) {
  const stored = bp => _.omitBy(_.get(widgetProps, `properties.${bp}.flexProperty`), _.isNil);
  const desktop = stored('desktop');
  if (breakpoint === 'mobile') {
    const mobile = _.omit(stored('mobile'), 'padding');
    const resolved = _.defaults({}, mobile, MOBILE_FLEX_DEFAULTS, desktop, baseDefaults);
    // Mobile padding always inherits desktop, capped to the mobile ceiling. Stored
    // mobile padding is ignored: resolved layout gets materialized into storage
    // (often as zeros), which would otherwise shadow the desktop value.
    resolved.padding = capPaddingSides(_.get(desktop, 'padding', _.get(baseDefaults, 'padding')));
    return resolved;
  }
  return _.defaults({}, desktop, baseDefaults);
}

export const FlexFlowContext = React.createContext({ flexMode: false, flexLayout: DEFAULT_FLEX_LAYOUT });

const FLEX_CONTAINERS = {
  [PAGE]: { baseDefaults: PAGE_DEFAULT_FLEX_LAYOUT },
  [SECTION_COLUMN]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
  [CONTAINER]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
  [CAROUSEL_CARD]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
};

export function isFlexContainerType(type) {
  return _.has(FLEX_CONTAINERS, type);
}

export { breaksOutOfFlexFlow } from './flexFlowRegistry';

export function isCascadeFlexTarget(type) {
  return isFlexContainerType(type) || type === CAROUSEL_CARD;
}

export function resolveFlexEnabled(widgetProps, breakpoint) {
  const desktop = _.get(widgetProps, 'properties.desktop.flexBoxEnabled', false);
  if (breakpoint !== 'mobile') return desktop;

  const overridden = _.get(widgetProps, 'properties.mobile.flexModeOverridden') === true;
  const mobileEnabled = _.get(widgetProps, 'properties.mobile.flexBoxEnabled');
  return (overridden || mobileEnabled === true) ? !!mobileEnabled : desktop;
}

export function flexBaseDefaults(type) {
  return _.get(FLEX_CONTAINERS, [type, 'baseDefaults'], DEFAULT_FLEX_LAYOUT);
}

export function resolveFlexMinHeight(widgetProps, breakpoint, baseDefaults = DEFAULT_FLEX_LAYOUT) {
  const resolved = resolveFlexLayout(widgetProps, breakpoint, baseDefaults);
  return parseFloat(_.get(resolved, 'minHeight', 0)) || 0;
}

export function resolveFlexMinHeightSource(widgetProps, breakpoint) {
  return _.get(widgetProps, `properties.${breakpoint}.flexProperty.minHeightSource`, null);
}

export function useFlexMinHeightCapture({
  editor, id, breakpoint, flexMode, dispatch,
  getElement = (root) => root,
  resolveFloor, resolveLayout, widgetProps, deps = [],
}) {
  const lastCommittedRef = React.useRef({});

  // Keep the latest widgetProps reachable from the long-lived ResizeObserver
  // callbacks below — otherwise they read a stale closure and can clobber a
  // just-committed user minHeight with an auto re-measurement.
  const widgetPropsRef = React.useRef(widgetProps);
  widgetPropsRef.current = widgetProps;

  React.useEffect(() => {
    if (!flexMode) return;

    const oppositeBreakpoint = breakpoint === 'desktop' ? 'mobile' : 'desktop';

    const canvasRoot = document.querySelector(`.editor_widget_scroll_container [data-widget-id="${id}"]`)
      || document.getElementById(id);
    const previewRoot = document.querySelector(`.editor_device_preview [data-widget-id="${id}"]`);
    const canvasEl = canvasRoot && getElement(canvasRoot);
    const previewEl = previewRoot && getElement(previewRoot);

    const isFloorResizing = () =>
      !!document.querySelector('[data-flex-floor-resizing="true"]');

    const measure = (el) => {
      if (!el) return 0;
      if (isFloorResizing()) return 0;
      // An empty flex container has no flowed content to record — its height is
      // purely user/default-driven, so auto-capture must not run (it would just
      // persist the empty-state floor and clobber a user-set minHeight).
      if (!el.querySelector('[data-widget-id]')) return 0;
      const prevMinHeight = el.style.minHeight;
      el.style.minHeight = '0px';
      const rendered = Math.round(el.scrollHeight);
      el.style.minHeight = prevMinHeight;
      return rendered;
    };

    const commit = (bp, rendered) => {
      if (!rendered || rendered <= 0) return;

      if (isFloorResizing()) return;

      const liveProps = widgetPropsRef.current;

      if (resolveFlexMinHeightSource(liveProps, bp) === 'user') return;

      const own = Math.round(resolveFloor(liveProps, bp));
      if (own === rendered) return;

      if (lastCommittedRef.current[bp] === rendered) return;

      const resolved = resolveLayout(liveProps, bp);
      const ownFlexProperty = _.get(liveProps, `properties.${bp}.flexProperty`, {});
      lastCommittedRef.current[bp] = rendered;
      dispatch(updateWidgetProperty(editor, id, bp, {
        flexProperty: { ...resolved, ...ownFlexProperty, minHeight: `${rendered}px`, minHeightSource: 'auto' },
      }));
    };

    const captureCanvas = () => commit(breakpoint, measure(canvasEl));
    const capturePreview = () => commit(oppositeBreakpoint, measure(previewEl));

    const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
      captureCanvas();
      capturePreview();
    }));

    const observers = [];
    if (canvasEl) {
      const o = new ResizeObserver(captureCanvas);
      o.observe(canvasEl);
      observers.push(o);
    }
    if (previewEl) {
      const o = new ResizeObserver(capturePreview);
      o.observe(previewEl);
      observers.push(o);
    }

    return () => {
      cancelAnimationFrame(raf);
      observers.forEach(o => o.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexMode, breakpoint, id, ...deps]);
}

export function fillMobileFlexDefaults(widget) {
  const type = _.get(widget, 'type');

  let resolved = null;
  if (type === CAROUSEL_CARD) {
    const config = getCardFlexConfig(widget, 'mobile');
    if (_.get(config, 'flexBoxEnabled')) resolved = config.flexProperty;
  } else if (isFlexContainerType(type) && resolveFlexEnabled(widget, 'mobile')) {
    resolved = resolveFlexLayout(widget, 'mobile', flexBaseDefaults(type));
  }

  if (!resolved) return widget;

  return {
    ...widget,
    properties: {
      ...widget.properties,
      mobile: {
        ...(widget.properties && widget.properties.mobile),
        flexProperty: resolved,
      },
    },
  };
}

export function flowsChildrenInFlex(type, widgetProps, breakpoint = 'desktop') {
  return isFlexContainerType(type) && resolveFlexEnabled(widgetProps, breakpoint);
}

export function allColumnsInFlexMode(columns) {
  return columns.length > 0 && columns.every(column => flowsChildrenInFlex(column.type, column));
}

export const CONTENT_DRIVEN_SECTION_HEIGHT = 'fit-content';

export const EMPTY_CONTENT_SECTION_MIN_HEIGHT = 200;

export function isStretchedWidget(widget) {
  return _.get(widget, 'properties.desktop.stretched', false);
}

export function enableFlexForInsertedSubtrees(widgets, rootIds) {
  const byId = _.keyBy(widgets, 'uid');
  const childrenByParent = _.groupBy(widgets, 'parent');

  const subtree = new Set();
  const walk = (id) => {
    if (subtree.has(id)) return;
    subtree.add(id);
    (childrenByParent[id] || []).forEach(child => walk(child.uid));
  };
  (rootIds || []).forEach(walk);

  const toEnable = new Set();
  subtree.forEach(uid => {
    const widget = byId[uid];
    if (!widget || !isCascadeFlexTarget(widget.type)) return;
    if (_.get(widget, 'properties.desktop.flexBoxEnabled', false)) return;
    if (isUnderFlexFlow(byId, widget)) toEnable.add(uid);
  });

  if (toEnable.size === 0) return widgets;

  return widgets.map(widget => toEnable.has(widget.uid)
    ? {
        ...widget,
        properties: {
          ...widget.properties,
          desktop: {
            ...(widget.properties && widget.properties.desktop),
            flexBoxEnabled: true,
            flexProperty: resolvedFlexPropertyFor(widget, 'desktop', (childrenByParent[widget.uid] || []).length > 0),
          }
        }
      }
    : widget);
}

function isActiveFlexFlowAncestor(widget) {
  if (!widget) return false;
  if (widget.type === CAROUSEL_CARD) {
    return _.get(getCardFlexConfig(widget, 'desktop'), 'flexBoxEnabled', false);
  }
  return flowsChildrenInFlex(widget.type, widget, 'desktop');
}

function isUnderFlexFlow(byId, widget) {
  let current = byId[widget.parent];
  while (current) {
    if (isActiveFlexFlowAncestor(current)) return true;
    current = byId[current.parent];
  }
  return false;
}

export const RESET_FLEX_CONTEXT = { flexMode: false, flexLayout: DEFAULT_FLEX_LAYOUT };

function descendantIds(widgets, rootId) {
  const childrenByParent = _.groupBy(widgets, 'parent');
  const result = [];
  const walk = (parentId) => {
    (childrenByParent[parentId] || []).forEach(child => {
      result.push(child.uid);
      walk(child.uid);
    });
  };
  walk(rootId);
  return result;
}

function eachDescendantFlexContainer(widgets, widgetId, fn) {
  const descendantSet = new Set(descendantIds(widgets, widgetId));
  widgets
    .filter(w => descendantSet.has(w.uid) && isCascadeFlexTarget(w.type))
    .forEach(fn);
}

export function flowedDescendants(widgets, rootId, breakpoint = 'desktop') {
  const subtree = new Set([rootId, ...descendantIds(widgets, rootId)]);
  const byId = _.keyBy(widgets, 'uid');
  return widgets.filter(w => {
    const parent = byId[w.parent];
    return parent && subtree.has(parent.uid) && flowsChildrenInFlex(parent.type, parent, breakpoint);
  });
}

export const EMPTY_FLEX_MIN_HEIGHT = 200;

function resolvedFlexPropertyFor(widget, breakpoint, hasChildren) {
  const ownFlexProperty = _.get(widget, `properties.${breakpoint}.flexProperty`, {});
  if (_.get(ownFlexProperty, 'minHeight') != null) return ownFlexProperty;

  const height = hasChildren
    ? (parseFloat(_.get(widget, `properties.${breakpoint}.layout.height`))
        || parseFloat(_.get(widget, 'properties.desktop.layout.height'))
        || 0)
    : EMPTY_FLEX_MIN_HEIGHT;
  if (height <= 0) return ownFlexProperty;

  return { ...ownFlexProperty, minHeight: `${Math.round(height)}px`, minHeightSource: 'auto' };
}

export function applyFlexModeToggle(dispatch, editorSession, widgets, widgetId, enabling, { cascade = false, capturedLayouts = [], breakpoint = 'desktop', flexModeOverridden = false, groupId = null } = {}) {
  const marker = flexModeOverridden ? { flexModeOverridden: true } : {};
  const byId = _.keyBy(widgets, 'uid');
  const hasChildren = (uid) => (widgets || []).some(w => w.parent === uid);

  if (!enabling) {
    capturedLayouts.forEach(({ uid, breakpoint: bp, layout }) => {
      dispatch(updateWidgetProperty(editorSession, uid, bp, { layout }, groupId));
    });

    dispatch(updateWidgetProperty(editorSession, widgetId, breakpoint, { flexBoxEnabled: false, ...marker }, groupId));
    if (cascade) {
      eachDescendantFlexContainer(widgets, widgetId, container => {
        if (!_.get(container, 'properties.desktop.flexBoxEnabled', false)) return;
        dispatch(updateWidgetProperty(editorSession, container.uid, 'desktop', { flexBoxEnabled: false }, groupId));
      });
    }
    return;
  }

  dispatch(updateWidgetProperty(editorSession, widgetId, breakpoint, {
    flexBoxEnabled: enabling,
    flexProperty: resolvedFlexPropertyFor(byId[widgetId], breakpoint, hasChildren(widgetId)),
    ...marker,
  }, groupId));

  if (!cascade) return;

  eachDescendantFlexContainer(widgets, widgetId, container => {
    if (_.get(container, 'properties.desktop.flexBoxEnabled', false)) return;
    dispatch(updateWidgetProperty(editorSession, container.uid, 'desktop', {
      flexBoxEnabled: true,
      flexProperty: resolvedFlexPropertyFor(container, 'desktop', hasChildren(container.uid)),
    }, groupId));
  });
}
